import { OrderErrorsResponse } from 'app/orders/order.model';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map, tap, flatMap, catchError } from 'rxjs/operators';
import { Api } from 'app/core/api.service';
import { ApplicationsService } from 'app/applications/applications.service';
import { Application } from 'app/applications/application.model';
import { fullName, ServiceType } from 'app/core/service-type.model';
import { IFilesForRequest } from 'app/files/file.model';
import { Order, OrderError } from './order.model';
import { ISOStringForCurrentTime } from 'app/core/date/functions';

export interface OrderCreateSettings {
  application?: Application;
  export_format: string;
  region: string;
  verification: string;
  serviceType: ServiceType;
  files: IFilesForRequest[];
}

@Injectable()
export class OrderService {
  constructor(
    private _api: Api,
    private _applicationsService: ApplicationsService
  ) {}

  create(
    settings: OrderCreateSettings | any,
    serviceType: ServiceType,
  ): Observable<Order> {
    const linkServiceType = fullName(serviceType);
    const applicationId = settings.application
      ? settings.application.id
      : undefined;
    const headers = this._applicationsService.headers(applicationId);
    const properties: any = {};
    if (settings.verification) {
      properties.verification_type = settings.verification;
    }
    if (serviceType === 'ic') {
      properties.region = settings.region;
      properties.export_format = settings.export_format;
    } else {
      const _temp = Object.assign({}, settings);
      delete _temp.serviceType;
      delete _temp.verification;
      delete _temp.files;
      Object.assign(properties, _temp);
    }

    return this._api
      .post(
        `/v2/task/capture/${linkServiceType}`,
        {
          files: settings.files,
          properties: properties
        },
        { headers }
      )
      .pipe(flatMap(response => this.get(response.id)));
  }

  /** Возвращает заказ по его идентификатору */
  get(
    orderId: string,
    application: Application = this._applicationsService.defaultApplication()
  ): Observable<Order> {
    const headers = this._applicationsService.headers(application.id);
    return this._api
      .get(`/v2/task/${orderId}`, { headers })
      .pipe(map(response => new Order(response)));
  }

  /** Возвращает ошибки таски */
  getError(
    orderId: string,
    applicationId: string = this._applicationsService.defaultApplication().id
  ): Observable<OrderErrorsResponse> {
    const headers = this._applicationsService.headers(applicationId);
    return this._api.get(`/v2/task/${orderId}/error`, { headers }).pipe(
      map(response => ({
        ...response,
        errors: response.errors.map(error => new OrderError(error))
      }))
    );
  }

  /** Удаляет заказ */
  remove(order: Order): Observable<void> {
    const headers = this._applicationsService.headers(order.application);
    return this._api
      .delete(`/v2/task/${order.id}`, { headers })
      .pipe(tap(() => (order.status = 'Deleted')));
  }

  /** Отмечает, что заказ проверифицирован и помещает его в статус InProgress */
  verified(orderId: string, applicationId: string): Observable<void> {
    const headers = this._applicationsService.headers(applicationId);
    return this._api.put(`/v2/task/${orderId}/action/complete`, null, {
      headers
    });
  }

  /** Получает статистику для usage report */
  consumptionMonthly(
    serviceType: ServiceType = null,
    from: Date,
    to: Date
  ): Observable<any> {
    const _to = new Date(
      to.getFullYear(),
      to.getMonth(),
      to.getDate(),
      23,
      59,
      59
    );
    const params = {
      from: ISOStringForCurrentTime(from),
      to: ISOStringForCurrentTime(_to)
    };
    if (serviceType) {
      return this._api.get(`/v2/task/${serviceType}/consumption/monthly`, {
        params
      });
    } else {
      return this._api.get(`/v2/task/consumption/monthly`, {
        params
      });
    }
  }

  /** Отмечает, что заказ проверифицирован и помещает его в статус InProgress */
  publicVerified(verificationId: string): Observable<void> {
    return this._api.put(
      `/v2/task/action/complete?verificationId=${verificationId}`
    );
  }
}
